import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import Chart from "react-apexcharts";
import moment from "moment";
import { IoChevronBackCircle, IoChevronForwardCircle } from "react-icons/io5";
import { mdiCurrentAc, mdiFlash, mdiLightningBolt } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import "../../assets/styles/app-device.css";

const PURPLE_HALF = "rgba(114, 62, 216, 0.5)";

// ApexCharts needs plain colours, so the chart reads the theme's when drawn.
const themeColor = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

const barOptions = (categories, xTitle, yTitle, format) => {
  const dark = document.documentElement.dataset.theme === "dark";
  const text = themeColor("--hx-text", "#000");
  return {
    chart: { toolbar: { show: false }, fontFamily: "Poppins, sans-serif", animations: { speed: 400 }, foreColor: text },
    colors: [PURPLE_HALF],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "60%", dataLabels: { position: "top" } } },
    dataLabels: { enabled: true, offsetY: -18, style: { fontSize: "10px", colors: [text] }, formatter: format },
    xaxis: { categories, title: { text: xTitle }, labels: { rotate: -45, style: { fontSize: "10px" } } },
    yaxis: { title: { text: yTitle }, labels: { formatter: (v) => Number(v).toFixed(2) } },
    grid: { borderColor: dark ? "#1d3445" : "#e8edf3" },
    tooltip: { theme: dark ? "dark" : "light", y: { formatter: format } },
  };
};

const ChartCard = ({ title, children }) => (
  <section className="hx-detail-panel" style={{ marginTop: 20 }}>
    <h2 className="hx-section-title" style={{ paddingTop: 0, fontSize: 18 }}>
      {title}
    </h2>
    {children}
  </section>
);

const Empty = ({ text = "No data" }) => <p className="hx-history-empty">{text}</p>;

// ---------- RunTime Monitoring (app/screens/RunTimeMonitoring) ----------

const PERIODS = [
  { key: "day", label: "Day", unit: "day", label2: "D MMM YYYY", tick: "HH:mm" },
  { key: "month", label: "Month", unit: "month", label2: "MMM YYYY", tick: "DD/MM" },
  { key: "year", label: "Year", unit: "year", label2: "YYYY", tick: "MMM" },
];

// kWh used by an appliance per hour, day or month, worked out from its run
// time and the power set on the Edit Device page, as the app does.
export function RunTimeMonitoring() {
  const location = useLocation();
  const navigate = useHistory();
  const toast = useToggleTop();
  const role = usePropertyRole();
  const device = location.state?.data;
  const [period, setPeriod] = useState(PERIODS[0]);
  const [date, setDate] = useState(() => moment());
  const [rows, setRows] = useState(null);
  const power = Number(device?.meta?.power) || 0;

  const load = useCallback(async () => {
    if (!device) return;
    setRows(null);
    try {
      const result = await UserService.getRuntime(period.key, device.applianceId, date.format("YYYY-M-D"));
      // The API doesn't return the buckets in time order.
      setRows(
        (Array.isArray(result) ? result : [])
          .map((item) => ({ run: Number(item.run_time) || 0, at: item.timebucket }))
          .sort((a, b) => new Date(a.at) - new Date(b.at))
      );
    } catch (err) {
      setRows([]);
    }
  }, [device, period, date]);

  useEffect(() => {
    load();
  }, [load]);

  if (!device) return <Redirect to="/dashboard" />;

  const kwh = (run) => ((run / 3600) * power) / 1000;
  const canGoForward = date.clone().add(1, period.unit).isSameOrBefore(moment(), period.unit);

  return (
    <Main header="stack" title="RunTime Monitoring">
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div className="hx-chips" role="tablist" aria-label="Period" style={{ margin: 0, justifyContent: "center" }}>
          {PERIODS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={item.key === period.key}
              className="hx-chip"
              style={item.key === period.key ? { background: "var(--hx-primary)", color: "#fff" } : { color: "var(--hx-primary)" }}
              onClick={() => setPeriod(item)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {power === 0 ? (
          <div className="hx-empty" style={{ marginTop: 20 }}>
            Power value is not configured. Please set the power value on the edit device page.
            <button
              type="button"
              className="hx-btn"
              onClick={() => {
                // As on the device's menu: the API only lets owners edit devices.
                if (role === "guest") toast("You have to be a owner to edit the device.", "red");
                else navigate.push("/editDevice", { data: device });
              }}
            >
              Edit Device
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 6, color: "var(--hx-primary)" }}>
              <button type="button" className="hx-icon-btn" aria-label={`Previous ${period.unit}`} onClick={() => setDate(date.clone().subtract(1, period.unit))}>
                <IoChevronBackCircle />
              </button>
              <span style={{ fontSize: 17, minWidth: 130, textAlign: "center" }}>{date.format(period.label2)}</span>
              <button
                type="button"
                className="hx-icon-btn"
                aria-label={`Next ${period.unit}`}
                disabled={!canGoForward}
                style={{ opacity: canGoForward ? 1 : 0.3 }}
                onClick={() => setDate(date.clone().add(1, period.unit))}
              >
                <IoChevronForwardCircle />
              </button>
            </div>
            <ChartCard title={`${device.name} usage`}>
              {rows === null ? (
                <div className="hx-skel" style={{ minHeight: 300 }} />
              ) : rows.length ? (
                <Chart
                  type="bar"
                  height={340}
                  series={[{ name: "kWh", data: rows.map((row) => Number(kwh(row.run).toFixed(2))) }]}
                  options={barOptions(
                    rows.map((row) => moment(row.at).format(period.tick)),
                    "Periods",
                    "Unit (KWh)",
                    (v) => Number(v).toFixed(2)
                  )}
                />
              ) : (
                <Empty />
              )}
            </ChartCard>
          </>
        )}
      </div>
    </Main>
  );
}

// ---------- Energy stats for power-measuring plugs (ISP_1, W1SP_1) ----------

// Live voltage, current and power plus the last hour, week and month of use
// (app/screens/EnergyMetering/EnergyMetering.js).
export function EnergyStats() {
  const location = useLocation();
  const device = location.state?.data;
  const [stats, setStats] = useState(null);
  const [hour, setHour] = useState(null);
  const [week, setWeek] = useState(null);
  const [month, setMonth] = useState(null);

  useEffect(() => {
    if (!device) return;
    // Sorted by time; the API doesn't guarantee the order.
    const list = (value, key) =>
      (Array.isArray(value) ? value : []).slice().sort((a, b) => new Date(a[key]) - new Date(b[key]));
    UserService.getLastStatebydeviceId(User.getPropertyId(), device.deviceId)
      .then((ls) => setStats(ls?.devices?.[device.applianceId] ?? {}))
      .catch(() => setStats({}));
    UserService.getWattsLastOneHourData(device.applianceId).then((r) => setHour(list(r, "time"))).catch(() => setHour([]));
    UserService.getKwhWeekData(device.applianceId).then((r) => setWeek(list(r, "timebucket"))).catch(() => setWeek([]));
    UserService.getKwhData(device.applianceId).then((r) => setMonth(list(r, "timebucket"))).catch(() => setMonth([]));
  }, [device]);

  const cards = useMemo(
    () => [
      { label: "Voltage", unit: "V", value: stats?.voltage_volts, icon: mdiFlash },
      { label: "Current", unit: "A", value: stats?.current_amps, icon: mdiCurrentAc },
      { label: "Power", unit: "W", value: stats?.power_watts, icon: mdiLightningBolt },
    ],
    [stats]
  );

  if (!device) return <Redirect to="/dashboard" />;

  const kwhChart = (rows) =>
    rows === null ? (
      <div className="hx-skel" style={{ minHeight: 280 }} />
    ) : rows.length ? (
      <Chart
        type="bar"
        height={300}
        series={[{ name: "kWh", data: rows.map((row) => parseFloat(row.kwh) || 0) }]}
        options={barOptions(rows.map((row) => moment(row.timebucket).format("DD/MM")), "Days", "Unit (KWh)", (v) => Number(v).toFixed(2))}
      />
    ) : (
      <Empty />
    );

  return (
    <Main header="stack" title="Energy Metering">
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <h2 className="hx-section-title">Electric Stats</h2>
        <div className="hx-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          {cards.map((card) => (
            <div key={card.label} className="hx-empty" style={{ minHeight: 120, gap: 4 }}>
              <MdiIcon path={card.icon} size={30} color="var(--hx-primary)" />
              <strong style={{ fontSize: 22 }}>{stats === null ? "…" : card.value ?? "—"}</strong>
              <span style={{ fontSize: 13 }}>
                {card.label} ({card.unit})
              </span>
            </div>
          ))}
        </div>

        <ChartCard title="Last 1 hour">
          {hour === null ? (
            <div className="hx-skel" style={{ minHeight: 280 }} />
          ) : hour.length ? (
            <Chart
              type="area"
              height={300}
              series={[{ name: "Watts", data: hour.map((row) => Number(row.power_watts) || 0) }]}
              options={{
                ...barOptions(hour.map((row) => moment(row.time).format("hh:mm")), "Time", "Watts", (v) => `${Number(v).toFixed(0)} W`),
                dataLabels: { enabled: false },
                stroke: { curve: "smooth", width: 2 },
                fill: { type: "gradient", gradient: { opacityFrom: 0.5, opacityTo: 0.05 } },
              }}
            />
          ) : (
            <Empty />
          )}
        </ChartCard>
        <ChartCard title="Last 1 Week">{kwhChart(week)}</ChartCard>
        <ChartCard title="Last 1 Month">{kwhChart(month)}</ChartCard>
      </div>
    </Main>
  );
}
